import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Check, X, Calculator } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { useStripeCheckout } from "@/hooks/billing";
import { useCurrency } from "@/contexts/currency";
import { formatPrice } from "@/config/pricing";
import { getCanonicalUrl } from "@/config/site";
import { createOrganizationSchema, createSoftwareApplicationSchema } from "@/config/seo";
import HomeFooter from "./HomeFooter";
import HomeNav from "./HomeNav";
import PricingCalculator from "@/components/pricing/PricingCalculator";
import CurrencySelector from "@/components/pricing/CurrencySelector";

const PricingPage = () => {
  const { checkout, loading, error } = useStripeCheckout();
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const { currency, currencyConfig } = useCurrency();
  const description =
    "Review Help Genie pricing for voice AI agents, including plans, features, call allowances and tools for support, sales and lead capture.";
  const organizationSchema = createOrganizationSchema();
  const softwareSchema = createSoftwareApplicationSchema(
    "Help Genie",
    description,
    getCanonicalUrl("/pricing")
  );

  const plans = [
    {
      name: "Free",
      price: formatPrice(0, currency),
      period: "forever",
      description: "Perfect for trying out Help Genie",
      features: [
        { text: "Up to 10 calls per month", included: true },
        { text: "1 Genie included", included: true },
        { text: "Set custom prompt for Genie", included: true },
        { text: "Set custom goals for Genie", included: true },
        { text: "Help Genie branding", included: true },
        { text: "Basic conversational analysis", included: true },
        { text: "Limited knowledge base documents", included: true },
        { text: "Your logo, colours & voice", included: false },
        { text: "Custom voice & personality", included: false },
        { text: "Lead capture & qualification", included: false },
        { text: "Phone number integration", included: false },
        { text: "Email notifications & reports", included: false },
        { text: "Pronunciation customization", included: false },
        { text: "Team collaboration tools", included: false },
        { text: "Priority support", included: false },
        { text: "Multiple genies", included: false },
        { text: "API access & webhooks", included: false },
      ],
      cta: "Start Free",
      popular: false,
    },
    {
      name: "Professional",
      price: formatPrice(currencyConfig.displayPrices.professional, currency),
      period: "per genie/month",
      description: "Everything you need for professional support",
      features: [
        { text: `30 calls included monthly, then ${formatPrice(currencyConfig.displayPrices.additionalCall, currency)} per additional call`, included: true },
        { text: "Your logo, colours & voice", included: true },
        { text: "Set custom prompt for Genie", included: true },
        { text: "Set custom goals for your Genie", included: true },
        { text: "Advanced conversational insights", included: true },
        { text: "10 knowledge base documents per Genie", included: true },
        { text: "Lead capture & qualification", included: true },
        { text: "Custom voice & personality", included: true },
        { text: "Multi-language support", included: true },
        { text: "Pronunciation customization", included: true },
        { text: "Email notifications & reports", included: true },
        { text: "Embeddable widget & QR codes", included: true },
        { text: "Phone number integration", included: true },
        { text: "Team collaboration tools", included: true },
        { text: "API access & webhooks", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Get Started",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For high-volume and custom requirements",
      features: [
        { text: "Everything in Professional, plus:", included: true },
        { text: "Unlimited calls", included: true },
        { text: "Unlimited genies", included: true },
        { text: "Unlimited knowledge base documents", included: true },
        { text: "Dedicated account manager", included: true },
        { text: "Custom SLA agreements", included: true },
        { text: "Advanced security & compliance", included: true },
        { text: "Custom integrations", included: true },
        { text: "White-glove onboarding", included: true },
        { text: "Priority 24/7 support", included: true },
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Help Genie Pricing | Voice AI Plans for Growing Businesses</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={getCanonicalUrl("/pricing")} />
        <meta property="og:title" content="Help Genie Pricing | Voice AI Plans for Growing Businesses" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalUrl("/pricing")} />
        <meta property="og:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Help Genie Pricing | Voice AI Plans for Growing Businesses" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
        <HomeNav />

      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="/insights-background.webp" alt="Pricing background" className="w-full h-full object-cover" />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-purple-900/80 to-pink-800/80"></div>
        </div>

        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Choose the perfect plan for your business. Start free and scale as you grow.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm">
            <a href="/help-genie-for-business-solutions" className="text-gray-100 hover:text-white transition-colors">
              Compare solutions
            </a>
            <a href="/playbooks" className="text-gray-100 hover:text-white transition-colors">
              See playbooks
            </a>
            <a href="/contact" className="text-gray-100 hover:text-white transition-colors">
              Contact sales
            </a>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="w-full pt-8 pb-20 bg-white">
        <div className="w-full max-w-screen-xl mx-auto px-8">
          {/* Currency Selector */}
          <div className="flex justify-center mb-10">
            <CurrencySelector size="lg" />
          </div>

          {error && (
            <div className="max-w-2xl mx-auto mb-8 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-destructive text-center">{error}</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-6 flex flex-col ${
                  plan.popular
                    ? "bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 shadow-xl"
                    : "bg-gray-50 border border-gray-200"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-3">
                    {plan.name !== "Enterprise" && <span className="text-sm text-gray-500 mr-1">{currency}</span>}
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">/{plan.period}</span>
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${feature.included ? "text-gray-900" : "text-gray-400"}`}>{feature.text}</span>
                    </li>
                  ))}
                </ul>

                {plan.name === "Professional" && (
                  <div className="mb-6 p-4 bg-white/50 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-gray-900 mb-4 text-sm">Pricing Details</h4>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-start justify-between text-sm">
                        <span className="text-gray-700">Additional calls:</span>
                        <span className="font-semibold text-gray-900 text-right">{formatPrice(currencyConfig.displayPrices.additionalCall, currency)} per call<br/>after first 30</span>
                      </div>
                      <div className="flex items-start justify-between text-sm">
                        <span className="text-gray-700">Onboarding fee:</span>
                        <div className="flex flex-col items-end gap-1.5">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded font-semibold whitespace-nowrap">For a Limited Time</span>
                          <span className="text-base font-semibold text-gray-900 line-through decoration-2 decoration-gray-400">
                            {formatPrice(currencyConfig.displayPrices.onboardingFee, currency)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-indigo-200">
                      <h5 className="font-semibold text-gray-900 mb-3 text-xs uppercase tracking-wide">Phone Numbers ({currency}/mo)</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Local:</span>
                          <span className="font-semibold text-gray-900">{formatPrice(currencyConfig.displayPrices.phoneLocal, currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Toll-Free:</span>
                          <span className="font-semibold text-gray-900">{formatPrice(currencyConfig.displayPrices.phoneTollFree, currency)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Prices vary by region</p>
                    </div>
                  </div>
                )}

<div className="mt-auto">
                  {plan.name === "Free" ? (
                    <a
                      href="/signup"
                      className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                        plan.popular
                          ? "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                          : "bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50"
                      }`}
                    >
                      {plan.cta}
                    </a>
                  ) : plan.name === "Enterprise" ? (
                    <a
                      href="/live-agent/business-onboarding"
                      className="block w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50"
                    >
                      {plan.cta}
                    </a>
                  ) : (
                    <button
                      onClick={() => checkout()}
                      disabled={loading}
                      className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                        plan.popular
                          ? "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                          : "bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50"
                      } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {loading ? "Processing..." : plan.cta}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Calculator CTA */}
          <div className="max-w-3xl mx-auto mt-12">
            <div className="relative rounded-2xl p-8 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Calculate Your Exact Cost</h3>
              <p className="text-gray-600 mb-6">
                Not sure what your monthly investment will be? Use our calculator to get a personalized estimate based on
                your specific needs.
              </p>
              <button
                onClick={() => setCalculatorOpen(true)}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
              >
                <Calculator className="w-5 h-5" />
                Open Pricing Calculator
              </button>
            </div>
          </div>
        </div>
      </section>

      <PricingCalculator isOpen={calculatorOpen} onClose={() => setCalculatorOpen(false)} />

      {/* Feature Comparison Section */}
      <section className="w-full py-20 bg-gray-50">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What You Get With Every Genie</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Each Help Genie comes packed with powerful features to transform your customer support
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Voice-First Interface</h3>
              <p className="text-sm text-gray-600">Natural conversations that feel human, powered by advanced voice technology</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics & Insights</h3>
              <p className="text-sm text-gray-600">Understand your customers better with conversation analytics</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lead Capture</h3>
              <p className="text-sm text-gray-600">Convert support interactions into qualified leads automatically</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Knowledge Base</h3>
              <p className="text-sm text-gray-600">Train your genie with your product documentation and FAQs</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Availability</h3>
              <p className="text-sm text-gray-600">Your genie never sleeps, providing support around the clock</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Integration</h3>
              <p className="text-sm text-gray-600">Embed on your website or share via QR code in minutes</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time Transcription</h3>
              <p className="text-sm text-gray-600">Get instant transcripts of every conversation for review and analysis</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Workflows</h3>
              <p className="text-sm text-gray-600">Design conversation flows tailored to your business needs</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sentiment Analysis</h3>
              <p className="text-sm text-gray-600">Understand customer emotions and satisfaction levels in real-time</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Multi-Platform Support</h3>
              <p className="text-sm text-gray-600">Works seamlessly on web, mobile, and phone channels</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Routing</h3>
              <p className="text-sm text-gray-600">Automatically categorize and route conversations based on content</p>
            </div>

            <div className="bg-white rounded-lg p-5 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Performance Metrics</h3>
              <p className="text-sm text-gray-600">Track response times, resolution rates, and customer satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Genie */}
      <section className="w-full py-16 md:py-24 bg-white">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center">
          <div className="md:w-1/2 text-center md:text-left mb-12 md:mb-0 md:pr-6 lg:pr-12">
            <div className="max-w-lg mx-auto md:mx-0 md:ml-auto">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-indigo-900 mb-4">Ready to Elevate Your Customer Experience?</h2>
              <p className="text-lg text-slate-600 mb-8">
                Give your customers instant voice support that's always available, always helpful
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a
                  href="/signup"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
                >
                  Start Free Today
                </a>
                <a
                  href="/live-agent/business-onboarding"
                  className="inline-block px-8 py-3 bg-white border-2 border-gray-300 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300 text-lg"
                >
                  Talk to Sales
                </a>
              </div>
            </div>
          </div>

          <div className="md:w-1/2 flex justify-center items-center">
            <div className="max-w-xs mx-auto">
              <div className="transform block scale-75 lg:scale-90">
                <GenieAvatar
                  speaking={true}
                  color="#9333ea"
                  secondaryColor="#2563eb"
                  gradientEnabled={true}
                  gradientAngle={135}
                  customAvatarUrl="/genie-mascots/role/concierge.png"
                  size={320}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

        <HomeFooter />
      </div>
    </>
  );
};

export default PricingPage;
